import React from 'react';

// Parsers y utilidades para el módulo de privilegios

// Módulos disponibles en el sistema
export const MODULOS = {
  sensor: 'Sensores',
  tarjeta: 'Tarjetas',
  proyecto: 'Proyectos',
  elemento: 'Elementos',
  logs: 'Logs',
  usuarios: 'Usuarios',
  empresas: 'Empresas',
  // dispositivo: 'Dispositivos', // Eliminado de la UI de privilegios
  graficas: 'Gráficas'
};

// Acciones disponibles
export const ACCIONES = {
  crear: 'Crear',
  consultar: 'Consultar',
  actualizar: 'Actualizar',
  eliminar: 'Eliminar',
  controlar: 'Controlar',
  all: 'Todas las acciones'
};

// Opciones para selectores
export const moduloOptions = Object.entries(MODULOS).map(([value, label]) => ({
  value,
  label
}));

export const accionOptions = Object.entries(ACCIONES).map(([value, label]) => ({
  value,
  label
}));

// Colores para módulos
export const getModuloColor = (modulo) => {
  const colors = {
    sensor: 'blue',
    tarjeta: 'green',
    proyecto: 'purple',
    elemento: 'orange',
    logs: 'cyan',
    usuarios: 'red',
    empresas: 'magenta',
    dispositivo: 'gold',
    graficas: 'volcano'
  };
  return colors[modulo] || 'default';
};

// Colores para acciones
export const getAccionColor = (accion) => {
  const colors = {
    crear: 'green',
    consultar: 'blue',
    actualizar: 'orange',
    eliminar: 'red',
    controlar: 'purple',
    all: 'gold'
  };
  return colors[accion] || 'default';
};

// Parser para privilegios
export const parserPrivilegio = (privilegio) => {
  if (!privilegio) return {};
  
  return {
    _id: privilegio._id,
    id: privilegio._id,
    usuario: privilegio.usuario,
    usuarioNombre: privilegio.usuario?.nombre || 'N/A',
    usuarioEmail: privilegio.usuario?.email || 'N/A',
    modulo: privilegio.modulo,
    moduloNombre: MODULOS[privilegio.modulo] || privilegio.modulo,
    acciones: privilegio.acciones || [],
    accionesNombres: privilegio.acciones?.map(accion => ACCIONES[accion] || accion).join(', ') || '',
    activo: privilegio.activo,
    vigencia: privilegio.vigencia,
    vigenciaDesde: privilegio.vigencia?.desde ? new Date(privilegio.vigencia.desde).toLocaleDateString() : 'N/A',
    vigenciaHasta: privilegio.vigencia?.hasta ? new Date(privilegio.vigencia.hasta).toLocaleDateString() : 'Sin vencimiento',
    esVigente: isPrivilegioVigente(privilegio),
    restricciones: privilegio.restricciones,
    observaciones: privilegio.observaciones || '',
    otorgadoPor: privilegio.otorgadoPor,
    createdAt: privilegio.createdAt ? new Date(privilegio.createdAt).toLocaleDateString() : 'N/A',
    updatedAt: privilegio.updatedAt ? new Date(privilegio.updatedAt).toLocaleDateString() : 'N/A'
  };
};

// Verificar si un privilegio está vigente
export const isPrivilegioVigente = (privilegio) => {
  if (!privilegio.activo) return false;
  
  const ahora = new Date();
  const desde = privilegio.vigencia?.desde ? new Date(privilegio.vigencia.desde) : new Date(0);
  const hasta = privilegio.vigencia?.hasta ? new Date(privilegio.vigencia.hasta) : null;
  
  return desde <= ahora && (!hasta || hasta >= ahora);
};

// Formatear fecha de vigencia
export const formatVigencia = (vigencia) => {
  if (!vigencia) return 'Sin restricciones';
  
  const desde = vigencia.desde ? new Date(vigencia.desde).toLocaleDateString() : 'No definido';
  const hasta = vigencia.hasta ? new Date(vigencia.hasta).toLocaleDateString() : 'Sin vencimiento';
  
  return `${desde} - ${hasta}`;
};

// Columnas para tabla de privilegios
export const columnsPrivilegios = [
  {
    title: 'Usuario',
    dataIndex: 'usuarioNombre',
    key: 'usuarioNombre',
    render: (text, record) => (
      <div>
        <div>{text}</div>
        <small style={{ color: '#8c8c8c' }}>{record.usuarioEmail}</small>
      </div>
    )
  },
  {
    title: 'Módulo',
    dataIndex: 'modulo',
    key: 'modulo',
    render: (modulo) => (
      <span style={{ 
        padding: '2px 8px', 
        borderRadius: '4px',
        backgroundColor: `var(--ant-${getModuloColor(modulo)}-1)`,
        color: `var(--ant-${getModuloColor(modulo)}-7)`,
        fontSize: '12px'
      }}>
        {MODULOS[modulo] || modulo}
      </span>
    )
  },
  {
    title: 'Acciones',
    dataIndex: 'acciones',
    key: 'acciones',
    render: (acciones) => (
      <div>
        {acciones.map(accion => (
          <span 
            key={accion}
            style={{ 
              padding: '2px 6px', 
              margin: '2px',
              borderRadius: '3px',
              backgroundColor: `var(--ant-${getAccionColor(accion)}-1)`,
              color: `var(--ant-${getAccionColor(accion)}-7)`,
              fontSize: '11px',
              display: 'inline-block'
            }}
          >
            {ACCIONES[accion] || accion}
          </span>
        ))}
      </div>
    )
  },
  {
    title: 'Estado',
    dataIndex: 'esVigente',
    key: 'esVigente',
    render: (esVigente) => (
      <span style={{ 
        color: esVigente ? '#52c41a' : '#ff4d4f',
        fontWeight: 'bold'
      }}>
        {esVigente ? '✅ Vigente' : '❌ Inactivo'}
      </span>
    )
  },
  {
    title: 'Vigencia',
    dataIndex: 'vigencia',
    key: 'vigencia',
    render: (vigencia) => (
      <small style={{ color: '#8c8c8c' }}>
        {formatVigencia(vigencia)}
      </small>
    )
  }
];

// Agrupar privilegios por usuario
export const agruparPrivilegiosPorUsuario = (privilegios) => {
  const agrupados = {};
  
  privilegios.forEach(privilegio => {
    const usuarioId = privilegio.usuario?._id || privilegio.usuario;
    if (!agrupados[usuarioId]) {
      agrupados[usuarioId] = {
        usuario: privilegio.usuario,
        privilegios: []
      };
    }
    agrupados[usuarioId].privilegios.push(privilegio);
  });
  
  return Object.values(agrupados);
};

// Obtener resumen de privilegios de un usuario
export const getResumenPrivilegiosUsuario = (privilegios) => {
  const resumen = {};
  
  privilegios.forEach(privilegio => {
    if (isPrivilegioVigente(privilegio)) {
      if (!resumen[privilegio.modulo]) {
        resumen[privilegio.modulo] = new Set();
      }
      privilegio.acciones.forEach(accion => {
        resumen[privilegio.modulo].add(accion);
      });
    }
  });
  
  // Convertir Sets a arrays
  Object.keys(resumen).forEach(modulo => {
    resumen[modulo] = Array.from(resumen[modulo]);
  });
  
  return resumen;
};

// Validaciones para formularios
export const validarPrivilegio = (privilegio) => {
  const errores = {};
  
  if (!privilegio.usuario) {
    errores.usuario = 'Debe seleccionar un usuario';
  }
  
  if (!privilegio.modulo) {
    errores.modulo = 'Debe seleccionar un módulo';
  }
  
  if (!privilegio.acciones || privilegio.acciones.length === 0) {
    errores.acciones = 'Debe seleccionar al menos una acción';
  }
  
  if (privilegio.vigencia?.desde && privilegio.vigencia?.hasta) {
    const desde = new Date(privilegio.vigencia.desde);
    const hasta = new Date(privilegio.vigencia.hasta);
    
    if (hasta <= desde) {
      errores.vigenciaHasta = 'La fecha de vencimiento debe ser posterior a la fecha de inicio';
    }
  }
  
  return errores;
};

// Datos iniciales para formularios
export const privilegioInicial = {
  usuario: '',
  modulo: '',
  acciones: [],
  activo: true,
  vigencia: {
    desde: new Date(),
    hasta: null
  },
  restricciones: {
    soloPropio: false,
    empresaId: null,
    proyectoIds: [],
    elementoIds: []
  },
  observaciones: ''
};
